import React from 'react';
import { Shield, Lock, Eye, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Container } from '../layout/Container';

export const SecurityPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-red-900 py-12">
      <Container>
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Security & Compliance
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Red Set implements enterprise-grade security measures and follows industry best practices 
            for responsible AI security testing and data protection.
          </p>
        </div>

        {/* Security Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card variant="red">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Authentication</h3>
              <p className="text-sm text-gray-700">Supabase-powered secure authentication with session management</p>
            </div>
          </Card>
          
          <Card variant="red">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Data Encryption</h3>
              <p className="text-sm text-gray-700">End-to-end encryption for all sensitive data and communications</p>
            </div>
          </Card>
          
          <Card variant="red">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">API Security</h3>
              <p className="text-sm text-gray-700">Server-side API key management with rate limiting and validation</p>
            </div>
          </Card>
        </div>

        {/* Security Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <Card variant="red">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Shield className="h-6 w-6 mr-3 text-red-500" />
              Security Features
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Input Sanitization</h4>
                  <p className="text-sm text-gray-700">DOMPurify-based sanitization prevents XSS and injection attacks</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Rate Limiting</h4>
                  <p className="text-sm text-gray-700">IP-based rate limiting prevents abuse and DoS attacks</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Secure Headers</h4>
                  <p className="text-sm text-gray-700">CSP, HSTS, and other security headers protect against attacks</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Data Validation</h4>
                  <p className="text-sm text-gray-700">Comprehensive server-side validation of all inputs</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">API Key Management</h4>
                  <p className="text-sm text-gray-700">Server-side API key storage (requires Netlify environment setup)</p>
                </div>
              </div>
            </div>
          </Card>

          <Card variant="red">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Lock className="h-6 w-6 mr-3 text-red-500" />
              Data Protection
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Row Level Security</h4>
                  <p className="text-sm text-gray-700">Supabase RLS ensures users only access their own data</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Audit Logging</h4>
                  <p className="text-sm text-gray-700">Comprehensive logging of all security events and assessments</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Data Retention</h4>
                  <p className="text-sm text-gray-700">Configurable data retention policies for compliance</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Export Controls</h4>
                  <p className="text-sm text-gray-700">Users can export and delete their assessment data</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900">Transparency</h4>
                  <p className="text-sm text-gray-700">Open source codebase for security review and auditing</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Compliance & Legal */}
        <Card variant="red" className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Compliance & Legal Notice
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2 text-amber-500" />
                Authorized Use Only
              </h4>
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  Red Set is designed exclusively for authorized security testing and research purposes. 
                  Users must ensure compliance with all applicable laws and regulations.
                </p>
                <p>
                  <strong>Requirements:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Proper authorization before testing any AI systems</li>
                  <li>Compliance with target system terms of service</li>
                  <li>Adherence to local and international laws</li>
                  <li>Responsible disclosure of discovered vulnerabilities</li>
                </ul>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-green-500" />
                Privacy & GDPR
              </h4>
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  Red Set implements privacy-by-design principles and GDPR compliance measures 
                  for European users and data protection.
                </p>
                <p>
                  <strong>Privacy Features:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Minimal data collection and processing</li>
                  <li>User consent for data processing activities</li>
                  <li>Right to data portability and deletion</li>
                  <li>Transparent privacy policy and data handling</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* Security Recommendations */}
        <Card variant="red">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Security Recommendations for Users
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <Lock className="h-8 w-8 text-red-500 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-2">API Key Security</h4>
              <p className="text-sm text-gray-700">
                Store API keys securely in Netlify environment variables. Never commit keys to version control.
              </p>
            </div>
            <div className="text-center">
              <Eye className="h-8 w-8 text-red-500 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-2">Monitoring</h4>
              <p className="text-sm text-gray-700">
                Monitor assessment activities and review security logs regularly for suspicious behavior.
              </p>
            </div>
            <div className="text-center">
              <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-2">Responsible Testing</h4>
              <p className="text-sm text-gray-700">
                Only test systems you own or have explicit permission to test. Follow responsible disclosure practices.
              </p>
            </div>
          </div>
        </Card>

        {/* Disclaimer */}
        <div className="mt-8 p-6 bg-red-900/20 border border-red-500/30 rounded-lg">
          <div className="flex items-start space-x-3">
            <XCircle className="h-6 w-6 text-red-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-bold text-red-300 mb-2">Important Disclaimer</h4>
              <p className="text-sm text-red-200">
                Users are solely responsible for ensuring their use of Red Set complies with all applicable laws, 
                regulations, and terms of service of target AI systems. The developers of Red Set are not liable 
                for any misuse of the platform or violations of third-party terms of service.
              </p>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};